import { useEffect } from 'react';

import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { Alert, Button, Input } from 'components';
import { IconCheck, Logo } from 'components/Icons';
import useMutationActiveAccount from 'hooks/auth/useMutationActiveAccount';
import { ResetPasswordTokenDto } from 'interfaces/authInterface';
import { authService } from 'services';

import './ActiveAccount.scss';

const ActiveAccount = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();

  const SECONDS_REDIRECT = 5 * 1000;

  const mutationActiveAccount = useMutationActiveAccount();

  const onSubmitHandler = async (values: ResetPasswordTokenDto, resetForm: () => void) => {
    mutationActiveAccount.mutate(values);
    resetForm();
  };

  // Form
  const { errors, touched, isValid, dirty, getFieldProps, handleSubmit, resetForm } = useFormik({
    initialValues: {
      password: '',
      password_repeat: ''
    },
    onSubmit: async (values) => {
      onSubmitHandler(values, resetForm);
    },
    validationSchema: Yup.object({
      password: Yup.string().required('ACTIVE_ACCOUNT.FORM.PASSWORD.ERRORS.REQUIRED'),
      password_repeat: Yup.string()
        .required('ACTIVE_ACCOUNT.FORM.PASSWORD_REPEAT.ERRORS.REQUIRED')
        .oneOf([Yup.ref('password'), null], t('ACTIVE_ACCOUNT.FORM.PASSWORD_REPEAT.ERRORS.NOT_SAME'))
    })
  });

  useEffect(() => {
    if (token) {
      authService.setLocalToken(token);
    }
  }, []);

  useEffect(() => {
    if (mutationActiveAccount.isSuccess) {
      setTimeout(() => {
        navigate('/login');
      }, SECONDS_REDIRECT);
    }
  }, [mutationActiveAccount.isSuccess]);

  return (
    <div className="active-account flex items-center justify-center">
      <div className="form md:w-col-80 sm:w-col-80 w-col-30 xs:w-col-80 ">
        <div className="flex items-center justify-center mb-4">
          <div className="logo text-center">
            <Logo height={60} width={60} />
            <div className="mt-1">ombreapp</div>
          </div>
        </div>
        <form noValidate onSubmit={handleSubmit}>
          {mutationActiveAccount.isSuccess && (
            <div className="my-4">
              <Alert
                icon={<IconCheck />}
                label={t('ACTIVE_ACCOUNT.SUCCESS.LABEL')}
                title={t('ACTIVE_ACCOUNT.SUCCESS.TITLE')}
                type="success"
              />
            </div>
          )}
          <h3 className="password-recovery__title">{t('ACTIVE_ACCOUNT.TITLE')}</h3>
          <div className="form-control">
            <Input
              label={t('ACTIVE_ACCOUNT.FORM.PASSWORD.LABEL')}
              type="password"
              {...getFieldProps('password')}
              error={errors.password}
              touched={touched.password}
            />
          </div>

          <div className="form-control">
            <Input
              label={t('ACTIVE_ACCOUNT.FORM.PASSWORD_REPEAT.LABEL')}
              type="password"
              {...getFieldProps('password_repeat')}
              error={errors.password_repeat}
              touched={touched.password_repeat}
            />
          </div>

          <div className="form-control text-center">
            <Button block disabled={!(isValid && dirty)} htmlType="submit" type="primary">
              {t('ACTIVE_ACCOUNT.FORM.BTN_SUBMIT')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ActiveAccount;
